import React, { useEffect, useState, useCallback } from "react";
import { WifiOff, RefreshCcw, CloudUpload } from "lucide-react";
import { pendingCount, subscribe, flushQueue } from "@/lib/offlineQueue";
import { getLastSync, onSync, formatLastSync } from "@/lib/lastSync";
import { toast } from "sonner";

export default function OfflineBanner() {
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(getLastSync());

  useEffect(() => onSync(setLastSync), []);

  const refresh = useCallback(async () => {
    setPending(await pendingCount());
  }, []);

  useEffect(() => {
    refresh();
    const off = subscribe(refresh);
    const onOnline = () => { setOnline(true); refresh(); };
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    const interval = setInterval(refresh, 15000);
    return () => {
      off();
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      clearInterval(interval);
    };
  }, [refresh]);

  const sync = async () => {
    setSyncing(true);
    try {
      const { flushed, failed } = await flushQueue();
      if (flushed > 0) toast.success(`${flushed} elementi sincronizzati.`);
      if (failed > 0 && flushed === 0) toast.error(`Impossibile sincronizzare: ${failed} in coda.`);
      await refresh();
    } finally {
      setSyncing(false);
    }
  };

  if (online && pending === 0) return null;

  return (
    <div
      className={`fixed left-0 right-0 z-40 flex items-center justify-between gap-3 px-4 py-2 text-sm font-medium shadow-md ${
        !online ? "bg-amber-500 text-white" : "bg-emerald-600 text-white"
      }`}
      style={{ top: 0 }}
      data-testid="offline-banner"
    >
      <div className="flex items-center gap-2">
        {!online ? <WifiOff className="w-4 h-4" /> : <CloudUpload className="w-4 h-4" />}
        <span data-testid="offline-banner-text">
          {!online
            ? (pending > 0
                ? `Sei offline · ${pending} da inviare quando torni online`
                : `Sei offline · consulti i dati${formatLastSync(lastSync) ? ` al ${formatLastSync(lastSync)}` : ""}`)
            : `Ho ${pending} elementi in attesa di invio`}
        </span>
      </div>
      {online && pending > 0 && (
        <button
          onClick={sync}
          disabled={syncing}
          className="flex items-center gap-1 bg-white/20 hover:bg-white/30 rounded-full px-3 py-1 text-xs font-semibold disabled:opacity-50"
          data-testid="offline-sync-btn"
        >
          <RefreshCcw className={`w-3 h-3 ${syncing ? "animate-spin" : ""}`} />
          {syncing ? "Sync…" : "Sincronizza"}
        </button>
      )}
    </div>
  );
}
